/*
 * Description:
 * ---------------------
 * Where connections come out — the plain-language view of anchor dispersion.
 *
 * The Obfuscation panel is where dispersion is CONFIGURED. This panel only
 * observes, and answers the one question that survives all six mechanisms: right
 * now, where does my traffic actually surface, and is it spread or is it not.
 *
 * The form follows the data's job. This is part-to-whole over two or three
 * long-named categories, so it is ONE stacked bar with direct labels rather than
 * a bar per exit — a reader compares segments of a whole in one pass, where
 * separate bars make them do arithmetic. The lead number is the volume-weighted
 * exit count, as a hero figure, because it is the single number the view exists
 * to report.
 *
 * Volume, never connection count: a count treats a keepalive and a 4 GB transfer
 * as one flow each, so three exits holding one connection apiece read as
 * perfectly spread while the one with the transfer saw the whole site. Both are
 * shown, and the count is labelled as the flattering one.
 * See cloud-anchor/pkg/edge/placement.go.
 *
 * ! file FlowExitsPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiChevronDown, FiChevronRight, FiRefreshCw, FiLock, FiAlertTriangle, FiSlash,
} from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { fmtBytes, fmtDuration, fmtNum } from "../utils/formatters";

const FLOW_LIMIT = 200;

/* Categorical slots in FIXED order, assigned to an exit by identity and never by
   rank — a share that changes must not repaint the exits. Three is the cap for
   this palette; a fourth exit falls back to the de-emphasis grey rather than a
   generated hue that no colour-blind reader could separate. */
const SERIES = ["#2a78d6", "#eb6834", "#1baf7a"];
const SERIES_FALLBACK = "#94A3B8";

const fmtAge = (s) => (s == null ? "—" : fmtDuration(s));

/* The verdict, in one clause. Deliberately short: the hero figure carries the
   magnitude, this only says what it means. */
function verdict(st, exitsCarrying) {
  if (!st?.available) return { text: "The encrypted path is not running.", tone: "bad" };
  if (!st.enabled) return { text: "Spreading is off — everything leaves by one exit.", tone: "bad" };
  if (st.effective === false) return { text: st.ineffective_reason || "Armed, but not acting on your traffic.", tone: "bad" };
  const eff = st.effective_anchors_bytes || 0;
  if (eff < 1.5) return { text: "One exit is seeing nearly all of your traffic.", tone: "bad" };
  if (eff < exitsCarrying - 0.5) return { text: "Spread, but the balance favours one exit.", tone: "warn" };
  return { text: "Evenly spread by volume.", tone: "good" };
}

/* One stacked bar = the whole of the traffic, split by exit.
   2px surface gaps between segments, 4px rounded data-end, square at the origin. */
function SplitBar({ exits, colorFor }) {
  const carrying = exits.filter((a) => (a.byte_share || 0) > 0.001);
  if (carrying.length === 0) {
    return <div className="fx-splitbar fx-splitbar--empty" aria-label="no volume yet" />;
  }
  return (
    <div className="fx-splitbar" role="img"
         aria-label={carrying.map((a) => `${a.addr || a.id} ${Math.round((a.byte_share || 0) * 100)} percent`).join(", ")}>
      {carrying.map((a) => (
        <div key={a.id} className="fx-seg"
             style={{ flexGrow: a.byte_share, background: colorFor(a.id) }}
             title={`${a.addr || a.id} — ${((a.byte_share || 0) * 100).toFixed(1)}% of volume, ${fmtBytes(a.bytes)}`} />
      ))}
    </div>
  );
}

/* One exit. The colour chip is the only place hue carries identity; every value
   beside it wears a text token, and the cover state ships as icon + label so it
   never depends on colour alone. */
function ExitRow({ a, color, open, onToggle, flows, flowsLoading, truncatedNote }) {
  const share = a.byte_share || 0;
  const coverOnly = (a.cover_share || 0) > 0.9;

  const facts = [];
  if (a.flows_placed != null) facts.push(`${fmtNum(a.flows_placed)} connections`);
  if (a.long_flows > 0) facts.push(`${fmtNum(a.long_flows)} frozen`);
  facts.push(fmtBytes(a.bytes || 0));

  return (
    <div className={`fx-row${open ? " is-open" : ""}`}>
      <button className="fx-row-head" onClick={onToggle} aria-expanded={open}>
        <span className="fx-chev">{open ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}</span>
        <span className="fx-chip" style={{ background: a.ready ? color : SERIES_FALLBACK }} aria-hidden="true" />
        <span className="fx-row-name">{a.addr || a.id}</span>
        {a.active && <span className="fx-tag">carrying the tunnel</span>}
        {!a.ready && <span className="fx-tag fx-tag--off"><FiSlash size={10} /> no keys</span>}
        <span className="fx-row-pct">{(share * 100).toFixed(0)}%</span>
      </button>

      <div className="fx-row-sub">
        {facts.join(" · ")}
        {coverOnly && (
          <span className="fx-status">
            <FiAlertTriangle size={11} /> almost all cover — an observer here reads the cover
            pattern, not your traffic
          </span>
        )}
      </div>

      {open && (
        <div className="fx-flows">
          {flowsLoading && <div className="fx-dim">loading connections…</div>}
          {!flowsLoading && flows.length === 0 && (
            <div className="fx-dim">No connections are placed here right now.</div>
          )}
          {!flowsLoading && flows.length > 0 && (
            <>
              <div className="fx-table-wrap">
                <table className="fx-table">
                  <thead>
                    <tr><th>From</th><th>To</th><th>Proto</th><th>Age</th><th>Idle</th><th>Volume</th></tr>
                  </thead>
                  <tbody>
                    {flows.map((f, i) => (
                      <tr key={`${f.src}:${f.src_port}-${f.dst}:${f.dst_port}-${i}`}>
                        <td><code>{f.src}:{f.src_port}</code></td>
                        <td><code>{f.dst}:{f.dst_port}</code></td>
                        <td>{f.proto}</td>
                        <td>
                          {fmtAge(f.age_sec)}
                          {f.frozen && (
                            <span className="fx-frozen" title="Older than one re-draw — keeps this exit until it closes">
                              <FiLock size={10} /> frozen
                            </span>
                          )}
                        </td>
                        <td>{fmtAge(f.idle_sec)}</td>
                        <td>{fmtBytes(f.bytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {truncatedNote && <div className="fx-dim fx-trunc">{truncatedNote}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function FlowExitsPanel({ nodeApiUrl, selectedNode }) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [openExit, setOpenExit] = useState(null);
  const [flows, setFlows] = useState([]);
  const [flowTotal, setFlowTotal] = useState(0);
  const [flowsLoading, setFlowsLoading] = useState(false);

  const load = useCallback(async () => {
    const url = nodeApiUrl("/obf/anchors");
    if (!url) return;
    try {
      const res = await fetchWithTimeout(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSt(await res.json());
      setErr("");
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [nodeApiUrl]);

  /* Connections are fetched only while a row is open. The table can hold tens of
     thousands of entries, so polling it whether or not anyone is looking would
     cost the edge real work for nothing. */
  const loadFlows = useCallback(async () => {
    const url = nodeApiUrl(`/obf/anchors/flows?limit=${FLOW_LIMIT}`);
    if (!url) return;
    setFlowsLoading(true);
    try {
      const res = await fetchWithTimeout(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setFlows(j.flows || []);
      setFlowTotal(j.total || 0);
    } catch {
      setFlows([]);
      setFlowTotal(0);
    } finally {
      setFlowsLoading(false);
    }
  }, [nodeApiUrl]);

  useEffect(() => { setSt(null); setLoading(true); setOpenExit(null); }, [selectedNode]);
  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, 5000);
  useEffect(() => { if (openExit) loadFlows(); }, [openExit, loadFlows]);

  /* Colour is keyed to a STABLE ordering of exit ids, not to display order — the
     rows below are sorted by share, and a share that changes must never repaint
     the exits it describes. */
  const colorFor = useMemo(() => {
    const ids = (st?.anchors || []).map((a) => a.id).sort();
    const m = new Map(ids.map((id, i) => [id, SERIES[i] ?? SERIES_FALLBACK]));
    return (id) => m.get(id) || SERIES_FALLBACK;
  }, [st]);

  const exits = useMemo(
    () => [...(st?.anchors || [])].sort((a, b) => (b.byte_share || 0) - (a.byte_share || 0)),
    [st]);
  const exitsCarrying = useMemo(() => exits.filter((a) => a.ready).length, [exits]);

  const head = useMemo(() => verdict(st, exitsCarrying), [st, exitsCarrying]);
  const flowsFor = useCallback((id) => flows.filter((f) => f.anchor === id), [flows]);

  const effBytes = st?.effective_anchors_bytes || 0;
  const effCount = st?.effective_anchors || 0;
  const frozen = st?.long_flows_total || 0;
  const placed = st?.flows || 0;

  if (loading) return <div className="fx-panel"><div className="fx-dim">Loading…</div></div>;

  return (
    <div className="fx-panel">
      {err && <div className="fx-err">Could not read exit state: {err}</div>}

      {/* Hero: the one number this view exists to report, with the verdict beside
          it. Proportional figures — tabular is for columns, not display sizes. */}
      <section className={`fx-hero fx-hero--${head.tone}`}>
        <div className="fx-hero-figure">
          <span className="fx-hero-num">{effBytes ? effBytes.toFixed(2) : "—"}</span>
          <span className="fx-hero-unit">
            {exitsCarrying > 0 ? `of ${exitsCarrying}` : ""}
          </span>
        </div>
        <div className="fx-hero-body">
          <div className="fx-hero-label">exits by volume</div>
          <div className="fx-hero-verdict">{head.text}</div>
        </div>
        <button className="fx-refresh" onClick={() => { load(); if (openExit) loadFlows(); }}>
          <FiRefreshCw size={12} /> Refresh
        </button>
      </section>

      <SplitBar exits={exits} colorFor={colorFor} />

      <div className="fx-rows">
        {exits.length === 0 && <div className="fx-dim">No exits are configured.</div>}
        {exits.map((a) => (
          <ExitRow
            key={a.id}
            a={a}
            color={colorFor(a.id)}
            open={openExit === a.id}
            onToggle={() => setOpenExit(openExit === a.id ? null : a.id)}
            flows={flowsFor(a.id)}
            flowsLoading={flowsLoading}
            truncatedNote={
              flowTotal > FLOW_LIMIT
                ? `Showing the ${FLOW_LIMIT} largest of ${fmtNum(flowTotal)} connections, heaviest first.`
                : ""
            }
          />
        ))}
      </div>

      {/* Context, one line instead of a tile row. The connection-count figure is
          shown BECAUSE it disagrees — naming it as the flattering number is the
          point, not a second opinion. */}
      {st?.available && (
        <p className="fx-note">
          {fmtNum(placed)} connections placed
          {frozen > 0 && <> · <strong>{fmtNum(frozen)} frozen</strong>, which a re-draw cannot move</>}
          {effCount > 0 && <> · by connection count this would read {effCount.toFixed(2)}, which is the flattering number</>}
          .
        </p>
      )}

      {st?.available && st.enabled && (
        <p className="fx-note">
          Evening out the volume:{" "}
          <strong>
            {(st.balance_bias ?? 1) <= (st.balance_bias_min ?? 1)
              ? "off — the draw is purely random"
              : `${st.balance_bias.toFixed(1)}× toward the lightest exit`}
          </strong>
          {st.balancing && " (currently acting)"}. Adjust it under Obfuscation → Spread across exits.
        </p>
      )}

      <p className="fx-note">
        A connection is pinned to one exit for its whole life — it cannot be split, because each
        exit presents its own public address. Re-drawing moves <em>new</em> connections only, so
        anything <strong>frozen</strong> stays where it is until it closes.
      </p>
    </div>
  );
}
